import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ShoppingBag, ArrowUpRight } from "lucide-react";

export default function Store() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configured, setConfigured] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/printful/products");
        if (cancelled) return;
        setProducts(res.data.products || []);
        setConfigured(res.data.configured !== false);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div data-testid="store-page">
      <section className="container-edge pt-16 pb-8">
        <p className="overline">The Shop</p>
        <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl max-w-4xl" style={{ fontFamily: "Playfair Display, serif" }}>
          Wear the California life.
        </h1>
        <p className="mt-6 text-soft max-w-2xl">
          California-inspired merch, printed on demand. A little salt, a little sun, and a piece
          of the LA spirit you can carry with you.
        </p>
      </section>

      <section className="section pt-12" data-testid="store-grid">
        {loading && (
          <div className="flex items-center gap-3 text-soft" data-testid="store-loading">
            <Loader2 size={18} className="animate-spin" /> Loading products…
          </div>
        )}

        {!loading && error && (
          <div className="border border-soft p-10 max-w-2xl" data-testid="store-error">
            <p className="overline text-red-500">Something happened</p>
            <h3 className="mt-3 text-2xl" style={{ fontFamily: "Playfair Display, serif" }}>
              The shop is taking a quick break.
            </h3>
            <p className="mt-4 text-soft text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && !configured && (
          <div className="border border-soft p-10 max-w-2xl">
            <p className="overline">Coming soon</p>
            <h3 className="mt-3 text-2xl" style={{ fontFamily: "Playfair Display, serif" }}>Merch drops in just a moment.</h3>
            <p className="mt-4 text-soft text-sm">We're connecting the storefront. Check back soon — or get in touch to be notified.</p>
            <Link to="/contact" className="btn-primary mt-6 inline-flex">Notify me</Link>
          </div>
        )}

        {!loading && !error && configured && products.length === 0 && (
          <div className="border border-soft p-10 max-w-2xl" data-testid="store-empty">
            <ShoppingBag size={28} className="text-sand" strokeWidth={1.25} />
            <p className="overline mt-6">First drop incoming</p>
            <h3 className="mt-3 text-3xl" style={{ fontFamily: "Playfair Display, serif" }}>
              The shop is loading its first collection.
            </h3>
            <p className="mt-4 text-soft text-sm">
              We're curating a small, beautiful drop. Add yourself to the notify list and you'll
              be among the first to know.
            </p>
            <Link to="/contact" className="btn-primary mt-8 inline-flex" data-testid="store-notify-btn">
              Notify me
            </Link>
          </div>
        )}

        {!loading && !error && products.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {products.map((p) => (
              <Link
                to={`/store/${p.id}`}
                key={p.id}
                className="editorial-card flex flex-col group"
                data-testid={`product-card-${p.id}`}
              >
                <div className="zoom-img bg-linen aspect-square overflow-hidden">
                  {p.thumbnail_url ? (
                    <img src={p.thumbnail_url} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-soft text-sm">No image</div>
                  )}
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-xl" style={{ fontFamily: "Playfair Display, serif" }}>{p.name}</h3>
                  <span className="mt-6 inline-flex items-center text-sm text-ocean group-hover:text-sand transition-colors self-start">
                    View product <ArrowUpRight size={14} className="ml-2" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
